/**
 * Catalogo de eventos rastreados. Mantenha esta lista sincronizada com o
 * tracking plan oficial em `docs/tracking-plan.md`. Use somente nomes em
 * snake_case com no maximo 40 chars, no formato `dominio_acao_objeto`.
 *
 * IMPORTANTE: nenhum nome de evento ou property pode conter PII (CPF, email,
 * telefone, valor absoluto em reais etc.). Veja `pii-policy.ts` para detalhes.
 */
export const AnalyticsEvent = {
  AppOpen: 'app_open',
  AppBackground: 'app_background',
  ScreenView: 'screen_view',

  AuthSignUpStarted: 'auth_signup_started',
  AuthSignUpSubmitted: 'auth_signup_submitted',
  AuthSignUpFailed: 'auth_signup_failed',
  AuthSignUpEmailValidationSent: 'auth_signup_email_validation_sent',
  AuthSignUpCountrySelected: 'auth_signup_country_selected',
  AuthSignUpPhoneSubmitted: 'auth_signup_phone_submitted',
  AuthSignUpEmailValidated: 'auth_signup_email_validated',
  AuthSignUpEmailValidationFailed: 'auth_signup_email_validation_failed',
  AuthSignUpCompleted: 'auth_signup_completed',

  AuthLoginAttempt: 'auth_login_attempt',
  AuthLoginSuccess: 'auth_login_success',
  AuthLoginFailure: 'auth_login_failure',
  AuthBiometricPrompted: 'auth_biometric_prompted',
  AuthBiometricSuccess: 'auth_biometric_success',
  AuthBiometricFailed: 'auth_biometric_failed',
  AuthBiometricActivated: 'auth_biometric_activated',
  AuthLogout: 'auth_logout',

  AuthForgotPasswordStarted: 'auth_forgot_password_started',
  AuthForgotPasswordRequested: 'auth_forgot_password_requested',
  AuthForgotPasswordFailed: 'auth_forgot_password_failed',
  AuthPasswordResetSubmitted: 'auth_password_reset_submitted',
  AuthPasswordResetCompleted: 'auth_password_reset_completed',
  AuthPasswordChangeStarted: 'auth_password_change_started',
  AuthPasswordChangeCompleted: 'auth_password_change_completed',

  AssetCreateStarted: 'asset_create_started',
  AssetFormStepCompleted: 'asset_form_step_completed',
  AssetCreateCompleted: 'asset_create_completed',
  AssetCreateAbandoned: 'asset_create_abandoned',
  AssetCreateFailed: 'asset_create_failed',

  AssetDetailsViewed: 'asset_details_viewed',
  AssetEditStarted: 'asset_edit_started',
  AssetEditCompleted: 'asset_edit_completed',
  AssetDeleteStarted: 'asset_delete_started',
  AssetDeleteConfirmed: 'asset_delete_confirmed',
  AssetDeleteCompleted: 'asset_delete_completed',

  TransactionChargeStarted: 'transaction_charge_started',
  TransactionChargeSubmitted: 'transaction_charge_submitted',
  TransactionChargeCompleted: 'transaction_charge_completed',
  TransactionChargeFailed: 'transaction_charge_failed',
  TransactionWithdrawStarted: 'transaction_withdraw_started',
  TransactionWithdrawSubmitted: 'transaction_withdraw_submitted',
  TransactionWithdrawCompleted: 'transaction_withdraw_completed',
  TransactionWithdrawFailed: 'transaction_withdraw_failed',
  TransactionCustomEventSubmitted: 'transaction_custom_event_submitted',
  TransactionCustomEventCompleted: 'transaction_custom_event_completed',

  HomeWalletCarouselSwiped: 'home_wallet_carousel_swiped',
  HomeSecureValueToggled: 'home_secure_value_toggled',
  HomeGoalEditOpened: 'home_goal_edit_opened',
  HomeGoalSaved: 'home_goal_saved',
  HomeGoalRegisterTapped: 'home_goal_register_tapped',
  HomeEmptyAddAssetTapped: 'home_empty_add_asset_tapped',

  OnboardingTourShown: 'onboarding_tour_shown',
  OnboardingTourStarted: 'onboarding_tour_started',
  OnboardingTourSkipped: 'onboarding_tour_skipped',
  OnboardingTourReplayRequested: 'onboarding_tour_replay_requested',

  NotificationReceivedForeground: 'notification_received_foreground',
  NotificationOpened: 'notification_opened',
  NotificationMarkedRead: 'notification_marked_read',

  InvestorProfileStarted: 'investor_profile_started',
  InvestorProfileStepCompleted: 'investor_profile_step_completed',
  InvestorProfileCompleted: 'investor_profile_completed',
  InvestorProfileAbandoned: 'investor_profile_abandoned',

  ProfileBiometricToggled: 'profile_biometric_toggled',
  ProfileAccountDeleteStarted: 'profile_account_delete_started',
  ProfileAccountDeleteConfirmed: 'profile_account_delete_confirmed',
  ProfileAccountDeleteCompleted: 'profile_account_delete_completed',
  ProfileExternalLinkOpened: 'profile_external_link_opened',
  ConsentAnalyticsChanged: 'consent_analytics_changed',

  TermsStatusRequested: 'terms_status_requested',
  TermsStatusFailed: 'terms_status_failed',
  TermsModalShown: 'terms_modal_shown',

  CtaTap: 'cta_tap',
  ErrorShown: 'error_shown',
  ErrorRequestFailed: 'error_request_failed',

  WebAnalysisViewed: 'web_analysis_viewed',
  WebAnalysisNavTapped: 'web_analysis_nav_tapped',
  WebAnalysisMenuOpened: 'web_analysis_menu_opened',
  WebAnalysisExported: 'web_analysis_exported',
  WebAnalysisPeriodChanged: 'web_analysis_period_changed',
  WebAnalysisFilterApplied: 'web_analysis_filter_applied',
  WebAnalysisGroupByChanged: 'web_analysis_group_by_changed',
} as const;

export type AnalysisType =
  | 'rentability'
  | 'evolution'
  | 'earnings'
  | 'projection'
  | 'fiis';

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

export type AssetType =
  | 'cripto'
  | 'debenture'
  | 'fund'
  | 'currency'
  | 'stock'
  | 'private_bonds'
  | 'treasure_bounds'
  | 'saving_account'
  | 'checking_account'
  | 'property'
  | 'vehicle'
  | 'customized';

export type LoginMethod = 'password' | 'google' | 'apple' | 'biometric';

export type FailureReason =
  | 'invalid_credentials'
  | 'already_registered'
  | 'network'
  | 'validation'
  | 'unknown';
